import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const PageTitleContext = createContext(null);

export function PageTitleProvider({ defaultTitle, children }) {
  const [title, setTitle] = useState(defaultTitle || "");
  const [source, setSource] = useState("default");
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [hideGlobalHeader, setHideGlobalHeader] = useState(false);

  useEffect(() => {
    if (source === "default") {
      setTitle(defaultTitle || "");
    }
  }, [defaultTitle, source]);

  const value = useMemo(
    () => ({
      title,
      setTitle,
      source,
      setSource,
      defaultTitle,
      isEditorMode,
      setIsEditorMode,
      hideGlobalHeader,
      setHideGlobalHeader,
    }),
    [title, source, defaultTitle, isEditorMode, hideGlobalHeader]
  );

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

export function usePageTitle(title) {
  const ctx = useContext(PageTitleContext);

  useEffect(() => {
    if (!ctx) return;
    if (title) {
      ctx.setTitle(title);
      ctx.setSource("custom");
    }
    return () => {
      if (!ctx) return;
      ctx.setSource("default");
    };
  }, [title]);

  return ctx;
}

// Hook to enable editor mode (hides large header, adjusts layout)
export function useEditorMode(enabled = true) {
  const ctx = useContext(PageTitleContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.setIsEditorMode(enabled);
    return () => {
      if (!ctx) return;
      ctx.setIsEditorMode(false);
    };
  }, [ctx, enabled]);

  return ctx;
}

// Route-local shell-mode flag: requests suppression of the global top
// header (notification bell / profile control) for specialized,
// full-height operational routes. Independent of editor mode — a route may
// use either, both, or neither. Same set-on-mount/reset-on-unmount
// lifecycle as useEditorMode, so no state can leak into a route mounted
// afterward and the global left navigation is never affected.
export function useHideGlobalHeader(enabled = true) {
  const ctx = useContext(PageTitleContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.setHideGlobalHeader(enabled);
    return () => {
      if (!ctx) return;
      ctx.setHideGlobalHeader(false);
    };
  }, [ctx, enabled]);

  return ctx;
}

export function usePageTitleContext() {
  return useContext(PageTitleContext);
}
